import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AUDIENCES = [
  { value: "all",          label: "Tous les utilisateurs" },
  { value: "talent",       label: "Talents uniquement" },
  { value: "professional", label: "Créateurs uniquement" },
  { value: "premium",      label: "Comptes Premium" },
];

/**
 * Inline admin control to set a concours/event as the "Dashboard hero".
 * - Shows a badge "Mis en avant" if the item is currently featured for any audience.
 * - Opens a small modal with audience picker, then PUTs /admin/dashboard-hero.
 *
 * Props:
 *  - kind: "contest" | "event"
 *  - targetId: id of the concours/event
 *  - compact: when true, render a smaller pill button instead of full button
 */
export default function DashboardHeroToggle({ kind, targetId, compact = false }) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState("all");
  const [loading, setLoading] = useState(false);
  const [heroes, setHeroes] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/dashboard-hero");
      setHeroes(Array.isArray(data) ? data : []);
    } catch { /* no-op */ }
    finally { setRefreshing(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Featured audiences where THIS target is the currently set hero.
  const myAudiences = heroes
    .filter((h) => h.kind === kind && h.target_id === targetId && !h.expired)
    .map((h) => h.audience);

  const isFeatured = myAudiences.length > 0;

  const setHero = async () => {
    setLoading(true);
    try {
      await api.put("/admin/dashboard-hero", { kind, target_id: targetId, audience });
      toast.success("Mis en avant sur le Dashboard");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const clearHero = async (aud) => {
    if (!window.confirm(`Retirer la mise en avant pour « ${audienceLabel(aud)} » ?`)) return;
    setLoading(true);
    try {
      await api.delete(`/admin/dashboard-hero/${aud}`);
      toast.success("Mise en avant retirée");
      refresh();
    } catch (e) {
      toast.error(e.userMessage || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap" data-testid={`hero-toggle-${kind}-${targetId}`}>
        {isFeatured && (
          <span
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-[#1A1A1A] text-[#E6C29A] font-semibold"
            data-testid={`hero-featured-badge-${targetId}`}
          >
            <Sparkles className="w-3 h-3" />
            Mis en avant
            {myAudiences.length === 1 && myAudiences[0] !== "all" ? ` · ${audienceLabel(myAudiences[0])}` : ""}
            {myAudiences.length > 1 ? ` · ${myAudiences.length} audiences` : ""}
          </span>
        )}
        <Button
          type="button"
          size={compact ? "sm" : "default"}
          variant={isFeatured ? "outline" : "default"}
          className={
            isFeatured
              ? "rounded-full border-[#EADDC9] text-[#1A1A1A] hover:bg-[#FAF3EA] h-8 text-xs"
              : "rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-8 text-xs"
          }
          onClick={() => setOpen(true)}
          data-testid={`hero-toggle-btn-${targetId}`}
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          {isFeatured ? "Modifier" : "Mettre en avant"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="hero-toggle-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1A1A1A]">
              Mettre en avant sur le Dashboard
            </DialogTitle>
            <p className="text-sm text-[#666] mt-1">
              Cet élément s'affichera en grande carte premium au sommet du Dashboard de l'audience choisie.
              Un seul élément peut être mis en avant par audience à la fois.
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="caption-label text-[#A26F3E]">Audience</label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="rounded-xl mt-2" data-testid="hero-audience-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCES.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Show what's currently featured for context */}
            {heroes.length > 0 && (
              <div className="rounded-xl border border-[#EADDC9] bg-[#FAF3EA]/40 p-4">
                <p className="caption-label text-[#A26F3E] mb-2">Mises en avant actuelles</p>
                <ul className="space-y-1.5 text-xs">
                  {heroes.map((h) => (
                    <li key={h.audience} className="flex items-center justify-between gap-2">
                      <span className="text-[#1A1A1A] truncate">
                        <span className="font-semibold">{audienceLabel(h.audience)}</span>
                        <span className="text-[#8B8680]"> · {h.kind === "contest" ? "Concours" : "Événement"} · {(h.target?.title || "—").slice(0, 40)}</span>
                        {h.expired && <span className="ml-1 text-[#B44F3F]">(expiré)</span>}
                      </span>
                      <button
                        type="button"
                        onClick={() => clearHero(h.audience)}
                        className="p-1 rounded hover:bg-white text-[#B44F3F]"
                        data-testid={`hero-clear-${h.audience}`}
                        title="Retirer"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="button"
              className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white"
              onClick={setHero}
              disabled={loading || refreshing}
              data-testid="hero-set-confirm"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function audienceLabel(v) {
  return AUDIENCES.find((a) => a.value === v)?.label || v;
}
